import { useCallback, useContext, useEffect, useState } from 'react';
import { useIcapsLogout } from './useIcapsLogout';
import { postUserReplacement } from '../network/endpoints/cart/postUserReplacement';
import { IdentityContext } from '../providers/IdentityProvider';

export const Actions = {
  LOG_OUT_BUSINESS_CUSTOMER: 'user/logoutBusinessCustomer',
  LOG_OUT_BUSINESS_CUSTOMER_SUCCESS: 'user/logoutBusinessCustomerSuccess',
  LOG_OUT_BUSINESS_CUSTOMER_FAIL: 'user/logoutBusinessCustomerFail',
} as const;

export type Actions = (typeof Actions)[keyof typeof Actions];

interface MoveBusinessCartItemsPayload {
  userId: string;
  provider: 'IRW' | 'Ciam';
}

export const useLogoutBusinessCustomer = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const icapsLogout = useIcapsLogout();
  const { getSession } = useContext(IdentityContext);

  const pubSubLoaded = useCallback(() => {
    if (window?.ikea?.pubsub) {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (document.readyState === 'complete') {
      pubSubLoaded();
    } else {
      window.addEventListener('load', pubSubLoaded);
      return () => {
        window.removeEventListener('load', pubSubLoaded);
      };
    }
  }, [pubSubLoaded]);

  const moveBusinessCartItemsCallback = useCallback(
    async (payload: MoveBusinessCartItemsPayload) => {
      try {
        await getSession(true);
        await postUserReplacement(payload.userId, payload.provider);
        window.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_SUCCESS);
      } catch {
        window.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [getSession],
  );

  const logoutBusinessCustomerCallback = useCallback(
    (payload: MoveBusinessCartItemsPayload) => {
      try {
        icapsLogout.logout(() => moveBusinessCartItemsCallback(payload));
      } catch {
        window.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
      }
    },
    [icapsLogout, moveBusinessCartItemsCallback],
  );

  useEffect(() => {
    if (icapsLogout.logoutState.state === 'error') {
      window.ikea.pubsub.publish(Actions.LOG_OUT_BUSINESS_CUSTOMER_FAIL);
    }
  }, [icapsLogout.logoutState.state]);

  useEffect(() => {
    if (isLoaded) {
      window.ikea.pubsub.subscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    }
    return () => {
      window.ikea?.pubsub?.unsubscribe(Actions.LOG_OUT_BUSINESS_CUSTOMER, logoutBusinessCustomerCallback);
    };
  }, [isLoaded, logoutBusinessCustomerCallback]);
};
